/**
 * H5 驾车规划：直调高德 Web 服务 REST（与后台 AMapHelper 同一类 Key）。
 * JSAPI Key 不能调 restapi（USERKEY_PLAT_NOMATCH），也不能在 localhost 走 Driving。
 * restapi 已允许 CORS，无需 webpack 代理、无需 WXJS。
 */

export var AMAP_STATION_ROUTE_STRATEGY = 2;
export var AMAP_ETA_ROUTE_STRATEGY = 0;
/** 后台 Web.config AMap:AppKeys 中当前可用的 Web 服务 Key，仅用于 REST 规划 */
export var AMAP_WEB_SERVICE_KEY = "8c14ed6ce233ce1692884f5496649ab5";

/** 高德官方路况色：畅通 / 缓行 / 拥堵 / 严重拥堵 / 无路况 */
export var TRAFFIC_COLOR_MAP = {
  smooth: "#00AF54",
  slow: "#FFD24D",
  congested: "#E94709",
  severe: "#8E0E0B",
  unknown: "#C4C4C4",
};

function toRad(d) {
  return (d * Math.PI) / 180;
}

export function haversineDistanceM(a, b) {
  var R = 6371000;
  var dLat = toRad(b.lat - a.lat);
  var dLng = toRad(b.lng - a.lng);
  var lat1 = toRad(a.lat);
  var lat2 = toRad(b.lat);
  var h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function estimateDurationByDistanceM(distanceM) {
  var speedMps = (30 * 1000) / 3600;
  return Math.max(60, Math.round(distanceM / speedMps));
}

export function formatDistanceKm(distanceM) {
  if (distanceM < 1000) {
    return Math.max(1, Math.round(distanceM)) + "米";
  }
  var km = distanceM / 1000;
  return (km < 10 ? km.toFixed(1) : Math.round(km)) + "公里";
}

export function formatDurationText(durationS) {
  var m = Math.max(1, Math.round(durationS / 60));
  if (m < 60) return m + "分钟";
  var h = Math.floor(m / 60);
  var mm = m % 60;
  return mm ? h + "小时" + mm + "分钟" : h + "小时";
}

function toPoint(p) {
  if (!p) return null;
  if (Array.isArray(p) && p.length >= 2) {
    var a0 = Number(p[0]);
    var a1 = Number(p[1]);
    if (isFinite(a0) && isFinite(a1)) return { lng: a0, lat: a1 };
    return null;
  }
  var lng = p.lng;
  var lat = p.lat;
  if (typeof lng === "function") lng = lng.call(p);
  if (typeof lat === "function") lat = lat.call(p);
  if ((lng == null || lat == null) && typeof p.getLng === "function") {
    lng = p.getLng();
    lat = p.getLat();
  }
  lng = Number(lng);
  lat = Number(lat);
  if (isFinite(lng) && isFinite(lat)) return { lng: lng, lat: lat };
  return null;
}

function pathToPoints(path) {
  if (!path) return [];
  if (typeof path === "string") {
    var fromStr = [];
    var parts = path.split(";");
    for (var s = 0; s < parts.length; s++) {
      var pair = parts[s].split(",");
      var lng = Number(pair[0]);
      var lat = Number(pair[1]);
      if (isFinite(lng) && isFinite(lat)) fromStr.push({ lng: lng, lat: lat });
    }
    return fromStr;
  }
  var points = [];
  var list = path || [];
  for (var i = 0; i < list.length; i++) {
    var pt = toPoint(list[i]);
    if (pt) points.push(pt);
  }
  return points;
}

export function normalizeTrafficStatus(raw) {
  var text = raw == null ? "" : String(raw).trim();
  if (!text) return "unknown";
  if (text === "1" || text.indexOf("畅通") >= 0 || text.indexOf("通畅") >= 0) return "smooth";
  if (text === "2" || text.indexOf("缓行") >= 0 || text.indexOf("缓慢") >= 0) return "slow";
  if (text === "4" || text.indexOf("严重") >= 0 || text.indexOf("非常") >= 0) return "severe";
  if (text === "3" || text.indexOf("拥堵") >= 0) return "congested";
  return "unknown";
}

export function mergeTrafficSegments(segments) {
  if (!segments || !segments.length) return [];
  var merged = [];
  for (var i = 0; i < segments.length; i++) {
    var seg = segments[i];
    if (!seg || !seg.points || seg.points.length < 2) continue;
    var last = merged[merged.length - 1];
    if (last && last.status === seg.status) {
      var first = seg.points[0];
      var lastPoint = last.points[last.points.length - 1];
      var samePoint =
        Math.abs(first.lng - lastPoint.lng) < 1e-6 && Math.abs(first.lat - lastPoint.lat) < 1e-6;
      last.points = last.points.concat(samePoint ? seg.points.slice(1) : seg.points);
    } else {
      merged.push({ status: seg.status, points: seg.points.slice() });
    }
  }
  return merged;
}

function emptyResult(error) {
  return { distanceM: 0, durationS: 0, points: [], error: error || "道路规划失败" };
}

var amapWebKey = AMAP_WEB_SERVICE_KEY;

export function setAmapWebKey(key) {
  amapWebKey = String(key || "").trim();
}

function formatLngLat(p) {
  return Number(p.lng).toFixed(6) + "," + Number(p.lat).toFixed(6);
}

function fetchAmapDrivingRouteRest(origin, destination, waypoints, strategy, key) {
  return new Promise(function (resolve) {
    if (!key) {
      resolve(emptyResult("未配置高德 Key"));
      return;
    }
    var params = [
      "key=" + encodeURIComponent(key),
      "origin=" + formatLngLat(origin),
      "destination=" + formatLngLat(destination),
      "extensions=all",
      "strategy=" + String(strategy != null ? strategy : AMAP_ETA_ROUTE_STRATEGY),
      "output=json",
    ];
    if (waypoints && waypoints.length) {
      params.push(
        "waypoints=" +
          waypoints
            .slice(0, 16)
            .map(formatLngLat)
            .join(";")
      );
    }
    var xhr = new XMLHttpRequest();
    xhr.open("GET", "https://restapi.amap.com/v3/direction/driving?" + params.join("&"), true);
    xhr.timeout = 12000;
    xhr.onload = function () {
      var text = xhr.responseText || "";
      if (!text || (text.charAt(0) !== "{" && text.charAt(0) !== "[")) {
        console.warn("[车辆地图] 高德 REST 返回非 JSON", xhr.status, text.slice(0, 120));
        resolve(emptyResult("驾车规划返回异常"));
        return;
      }
      try {
        var data = JSON.parse(text);
        var path = data && data.route && data.route.paths && data.route.paths[0];
        if (String(data.status) !== "1" || !path) {
          var errInfo = (data && (data.info || data.infocode)) || "规划失败";
          console.warn("[车辆地图] 高德 REST 规划失败", errInfo, data && data.infocode);
          resolve(emptyResult(errInfo));
          return;
        }
        var points = [];
        var steps = Array.isArray(path.steps) ? path.steps : [];
        for (var i = 0; i < steps.length; i++) {
          points = points.concat(pathToPoints(steps[i].polyline));
        }
        if (!points.length && path.polyline) points = pathToPoints(path.polyline);
        var trafficSegments = [];
        for (var s = 0; s < steps.length; s++) {
          var raw = steps[s].tmcs || [];
          var tmcs = Array.isArray(raw) ? raw : [];
          for (var t = 0; t < tmcs.length; t++) {
            var pts = pathToPoints(tmcs[t].polyline || tmcs[t].path);
            if (pts.length < 2) continue;
            trafficSegments.push({
              status: normalizeTrafficStatus(tmcs[t].status),
              points: pts,
            });
          }
        }
        resolve({
          distanceM: Number(path.distance) || 0,
          durationS: Number(path.duration) || 0,
          points: points,
          trafficSegments: trafficSegments.length ? trafficSegments : undefined,
        });
      } catch (e) {
        resolve(emptyResult("规划结果解析失败"));
      }
    };
    xhr.onerror = function () {
      resolve(emptyResult("驾车规划请求失败"));
    };
    xhr.ontimeout = function () {
      resolve(emptyResult("驾车规划超时"));
    };
    xhr.send();
  });
}

/**
 * 途经点超过 16 个时分段，拼接沿路折线后由页面自己画。
 */
export function fetchAmapDrivingRouteChain(points, options) {
  if (!points || points.length < 2) {
    return Promise.resolve(emptyResult("站点不足，无法规划线路"));
  }
  var strategy = options && options.strategy != null ? options.strategy : AMAP_ETA_ROUTE_STRATEGY;
  var merged = [];
  var mergedTraffic = [];
  var distanceM = 0;
  var durationS = 0;
  var lastError = "";
  var CHUNK = 17;

  function runFrom(start) {
    if (start >= points.length - 1) {
      if (!distanceM && !durationS && !merged.length) {
        return Promise.resolve(emptyResult(lastError || "道路规划失败"));
      }
      return Promise.resolve({
        distanceM: distanceM,
        durationS: durationS,
        points: merged,
        trafficSegments: mergedTraffic.length ? mergedTraffic : undefined,
      });
    }
    var end = Math.min(start + CHUNK, points.length - 1);
    var req = fetchAmapDrivingRouteRest(
      points[start],
      points[end],
      points.slice(start + 1, end),
      strategy,
      (options && options.key) || amapWebKey || AMAP_WEB_SERVICE_KEY
    );
    return req.then(function (result) {
      if (result && result.points && result.points.length >= 2) {
        if (merged.length) merged = merged.concat(result.points.slice(1));
        else merged = merged.concat(result.points);
        if (result.trafficSegments && result.trafficSegments.length) {
          mergedTraffic = mergedTraffic.concat(result.trafficSegments);
        }
        distanceM += result.distanceM || 0;
        durationS += result.durationS || 0;
      } else {
        lastError = (result && result.error) || "道路规划失败";
      }
      return runFrom(start + CHUNK);
    });
  }

  return runFrom(0);
}
